import { type ChangeEvent, useCallback, useEffect, useState } from 'react';

import { clamp, isEmpty, isNotDefined } from '@enterprise/core';

import type { TargetInputCellProps } from '@components/nth-calculator/components/types/target-input-cell.props';

import type { UseTargetInputCellConfigurationResult } from './types/target-input-cell-configuration.type';

export const useTargetInputCellConfiguration = ({
  context,
  data,
}: Pick<TargetInputCellProps, 'context' | 'data'>): UseTargetInputCellConfigurationResult => {
  const [localValue, setLocalValue] = useState<string>(data?.newTarget.toString() ?? '');

  const handleBlur = () => {
    if (isNotDefined(data)) {
      return;
    }

    if (isEmpty(localValue)) {
      context.handleRowUpdate(data.mailingListId, {
        nthValue: null,
        selectedTwentileValues: [],
      });

      return;
    }

    const parsedTarget = Number.parseFloat(localValue);

    if (!Number.isFinite(parsedTarget)) {
      context.handleRowUpdate(data.mailingListId, {
        nthValue: null,
        selectedTwentileValues: [],
      });

      return;
    }

    // const clamped = Math.min(Math.max(parsedTarget, 0), data.receivedQuantity);
    const clamped = clamp(parsedTarget, 0, data.firstOutput);

    context.handleRowUpdate(data.mailingListId, {
      nthValue: data.firstOutput - clamped,
      selectedTwentileValues: [],
    });
  };

  const handleChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setLocalValue(e.target.value);
  }, []);

  const handleClear = () => {
    if (isNotDefined(data)) {
      return;
    }

    setLocalValue('');
    context.handleRowUpdate(data.mailingListId, { nthValue: null, selectedTwentileValues: [] });
  };

  useEffect(() => {
    setLocalValue(data?.newTarget.toString() ?? '');
  }, [data?.newTarget]);

  return { handleBlur, handleChange, handleClear, localValue };
};
